/**
 * Shared utilities for radar chart data and presentation
 *
 * Provides functions for extracting player scores, converting to
 * Victory chart format, chart dimension/axis styling, and
 * dashboard card theming.
 * @module utils/radarChartUtils
 */

import { Platform, Dimensions } from "react-native";

/**
 * Chart color constants
 */
export const TEAM_COLOR = "#f97316";
export const LEAGUE_COLOR = "#6b7280";

/**
 * Radar chart category labels for player metrics
 */
export const RADAR_CATEGORY_LABELS = [
  "GPR",
  "Passing",
  "Dribbling",
  "Carrying",
  "Shooting",
] as const;

/**
 * Default maximum value for radar chart domain
 */
export const DEFAULT_RADAR_MAX = 100;

/**
 * Ring increment step for calculating tick values
 */
const RING_STEP = 25;

/**
 * Calculate a clean maximum value for chart domains
 * Rounds up to nearest multiple of 25 when data exceeds the default max.
 * @param dataMax - The maximum data value observed
 * @param defaultMax - The default maximum (used when data fits within it)
 * @returns A clean ceiling value (multiple of 25) that accommodates all data
 */
export const calculateNiceMaxValue = (
  dataMax: number,
  defaultMax: number = DEFAULT_RADAR_MAX
): number => {
  if (!Number.isFinite(dataMax) || dataMax <= defaultMax) return defaultMax;
  return Math.ceil(dataMax / RING_STEP) * RING_STEP;
};

/**
 * Ring values for radar chart concentric circles
 * These map to league position benchmarks:
 * - 25 = Last in league
 * - 50 = Middle of league
 * - 75 = 5th in league
 * - 100 = Best in league (1st)
 */
export const RADAR_RING_VALUES = [25, 50, 75, 100] as const;

/**
 * Ring labels showing league position benchmarks
 * Used to help users contextualize team performance against league standings
 */
export const RADAR_RING_LABELS: Record<number, string> = {
  25: "Last",
  50: "Middle",
  75: "5th",
  100: "1st",
} as const;

/**
 * Radar data point for Victory chart
 */
export interface RadarDataPoint {
  readonly x: number;
  readonly y: number;
  readonly label: string;
}

/**
 * Player bio data with categorical scores
 */
export interface PlayerBioData {
  readonly carryingTransGlobalCategoricalScore?: number | null;
  readonly dribblingTransGlobalCategoricalScore?: number | null;
  readonly passingTransGlobalCategoricalScore?: number | null;
  readonly shotLocationTransGlobalCategoricalScore?: number | null;
  readonly gpr?: number | null;
  readonly age?: number | null;
  readonly playerValuation?: number | null;
  readonly isInjured?: boolean | null;
  readonly contractExpires?: string | null;
  readonly fitScore?: number | null;
}

/**
 * Team data for player
 */
export interface PlayerTeam {
  readonly id: string;
  readonly name: string;
}

/**
 * Player with categorical scores interface
 */
export interface PlayerWithScores {
  readonly bioData?: PlayerBioData | null;
  readonly team?: PlayerTeam | null;
}

/**
 * Player option for dropdown selection
 */
export interface PlayerOption {
  readonly id: string;
  readonly name: string;
  readonly position?: string | null;
  readonly gpr?: number | null;
}

/**
 * Satoshi font family for consistent design system styling
 */
const SATOSHI_FONT_FAMILY = "Satoshi, sans-serif";

/**
 * Radar chart axis styles
 */
export interface RadarAxisStyles {
  readonly dependentAxisStyle: Readonly<{
    axis: { stroke: string };
    grid: { stroke: string; strokeWidth: number };
    tickLabels: { fill: string };
  }>;
  readonly independentAxisStyle: Readonly<{
    axis: { stroke: string; strokeWidth: number };
    grid: { stroke: string; strokeWidth: number };
    tickLabels: {
      fill: string;
      fontSize: number;
      fontFamily: string;
      fontWeight: string;
      padding: number;
    };
  }>;
  readonly scatterStrokeColor: string;
}

/**
 * Chart dimensions type
 */
export interface ChartDimensions {
  readonly width: number;
  readonly height: number;
}

/**
 * Score order configuration for radar chart
 */
type ScoreOrder = "gpr-first" | "gpr-last";

/**
 * Extract categorical scores from a player
 * @param player - Player object with score fields
 * @param order - Order of scores: 'gpr-first' puts GPR at start, 'gpr-last' puts GPR at end
 * @returns Array of 5 score values (GPR, passing, dribbling, carrying, shooting) or nulls if player is missing
 */
export const extractScores = (
  player: PlayerWithScores | null | undefined,
  order: ScoreOrder = "gpr-last"
): readonly (number | null)[] => {
  if (!player) return [null, null, null, null, null];
  const bioData = player.bioData;
  const gpr = bioData?.gpr ?? null;
  const passing = bioData?.passingTransGlobalCategoricalScore ?? null;
  const dribbling = bioData?.dribblingTransGlobalCategoricalScore ?? null;
  const carrying = bioData?.carryingTransGlobalCategoricalScore ?? null;
  const shooting = bioData?.shotLocationTransGlobalCategoricalScore ?? null;

  if (order === "gpr-first") {
    return [gpr, passing, dribbling, carrying, shooting];
  }
  return [carrying, dribbling, passing, shooting, gpr];
};

/**
 * Convert averages to Victory radar data format
 * @param averages - Array of average values
 * @param categoryLabels - Labels for each category
 * @returns Array of RadarDataPoint objects with x, y, and label properties for Victory chart
 */
export const toRadarData = (
  averages: readonly number[],
  categoryLabels: readonly string[]
): readonly RadarDataPoint[] =>
  averages.map((value, index) => ({
    x: index + 1,
    y: Math.round(value),
    label: categoryLabels[index],
  }));

/**
 * Convert single player scores to radar data
 * @param player - Player object with scores
 * @param categoryLabels - Labels for each category
 * @param order - Order of scores for extraction
 * @returns Array of RadarDataPoint objects for rendering player metrics on radar chart
 */
export const playerToRadarData = (
  player: PlayerWithScores | null | undefined,
  categoryLabels: readonly string[],
  order: ScoreOrder = "gpr-last"
): readonly RadarDataPoint[] => {
  const scores = extractScores(player, order);
  return scores.map((score, index) => ({
    x: index + 1,
    y: Math.round(score ?? 0),
    label: categoryLabels[index],
  }));
};

/**
 * Get chart dimensions based on platform
 * @param webWidth - Width for web platform
 * @param webHeight - Height for web platform
 * @param mobileMaxWidth - Max width for mobile
 * @param mobileHeight - Height for mobile
 * @returns Platform-appropriate width and height for chart rendering
 */
export const getChartDimensions = (
  webWidth: number,
  webHeight: number,
  mobileMaxWidth: number,
  mobileHeight: number
): ChartDimensions => {
  if (Platform.OS === "web") {
    return { width: webWidth, height: webHeight };
  }
  const screenWidth = Dimensions.get("window").width;
  return {
    width: Math.min(screenWidth - 48, mobileMaxWidth),
    height: mobileHeight,
  };
};

/**
 * Get axis styles for radar chart
 * @param isDark - Dark theme flag
 * @returns Theme-appropriate styles for dependent and independent axes
 */
export const getAxisStyles = (isDark: boolean): RadarAxisStyles => {
  const gridColor = isDark ? "#374151" : "#e5e7eb";
  const labelColor = isDark ? "#9ca3af" : "#6b7280";

  return {
    dependentAxisStyle: {
      axis: { stroke: "none" },
      grid: { stroke: gridColor, strokeWidth: 1 },
      tickLabels: { fill: "none" },
    },
    independentAxisStyle: {
      axis: { stroke: gridColor, strokeWidth: 1 },
      grid: { stroke: gridColor, strokeWidth: 1 },
      tickLabels: {
        fill: labelColor,
        fontSize: 11,
        fontFamily: SATOSHI_FONT_FAMILY,
        fontWeight: "500",
        padding: 15,
      },
    },
    scatterStrokeColor: isDark ? "#1f2937" : "#ffffff",
  };
};

/**
 * Dashboard card theme classes
 */
export interface DashboardCardTheme {
  readonly containerClass: string;
  readonly headerBorderClass: string;
  readonly headingClass: string;
  readonly errorBgClass: string;
  readonly errorIconClass: string;
  readonly emptyBgClass: string;
  readonly emptyIconClass: string;
  readonly emptyTitleClass: string;
}

/**
 * Get theme classes for dashboard cards
 * @param isDark - Dark theme flag
 * @returns Object containing Tailwind CSS class strings for card styling
 */
export const getDashboardCardTheme = (isDark: boolean): DashboardCardTheme => ({
  containerClass: "border border-outline-subtle bg-surface-base",
  headerBorderClass: isDark ? "" : "border-b border-surface-muted/20",
  headingClass: isDark ? "text-white" : "text-content-primary",
  errorBgClass: "bg-status-error-surface",
  errorIconClass: "text-status-error",
  emptyBgClass: isDark
    ? "bg-needs-semantic-primary-500/20"
    : "bg-needs-semantic-primary-500/10",
  emptyIconClass: isDark
    ? "text-needs-semantic-primary-400"
    : "text-needs-semantic-primary-500",
  emptyTitleClass: isDark ? "text-white" : "text-content-primary",
});

/**
 * League average series configuration for radar charts
 */
export const LEAGUE_SERIES_CONFIG = {
  color: LEAGUE_COLOR,
  fillOpacity: 0.15,
  strokeWidth: 1.5,
  dashed: true,
} as const;

/**
 * Check if contract expires within 12 months
 * @param contractExpires - Contract expiration date string
 * @returns True if contract expires within one year, false otherwise or if date is missing
 */
export const isContractExpiringSoon = (
  contractExpires: string | null | undefined
): boolean => {
  if (!contractExpires) return false;
  const expiryDate = new Date(contractExpires);
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  return expiryDate <= oneYearFromNow;
};

/**
 * Format total value for display in Euros
 * @param value - Total value in currency units
 * @returns Formatted currency string with appropriate unit suffix (B, M, K)
 */
export const formatValue = (value: number): string => {
  if (value >= 1000000000) {
    return `€${(value / 1000000000).toFixed(3)}B`;
  }
  if (value >= 1000000) {
    return `€${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `€${(value / 1000).toFixed(0)}K`;
  }
  return `€${value.toFixed(0)}`;
};

/**
 * Format a number as an ordinal (1st, 2nd, 3rd, etc.)
 * @param n - The number to format
 * @returns Number with ordinal suffix (e.g., "1st", "2nd", "3rd", "4th")
 */
export const formatOrdinal = (n: number): string => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = Math.abs(Math.floor(n));
  const lastTwo = value % 100;
  const lastOne = value % 10;
  const suffix =
    lastTwo >= 11 && lastTwo <= 13 ? "th" : (suffixes[lastOne] ?? suffixes[0]);
  return `${value}${suffix}`;
};
